import { ArrowRight, Loader2 } from 'lucide-react';

import { Button, type ButtonProps } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface SubmitButtonProps extends ButtonProps {
  loading?: boolean;
  loadingLabel?: string;
}

/**
 * Primary form CTA — full width, premium gradient in dark mode, with a loading
 * spinner. Keeps its label mounted while loading so the accessible name is stable.
 */
export function SubmitButton({
  children,
  loading = false,
  loadingLabel,
  disabled,
  className,
  ...props
}: SubmitButtonProps) {
  return (
    <>
      <Button
        type="submit"
        size="lg"
        disabled={loading || disabled}
        aria-busy={loading || undefined}
        className={cn(
          'group mt-2 w-full dark:bg-gradient-to-b dark:from-[hsl(210_95%_64%)] dark:to-[hsl(212_100%_56%)] dark:text-[hsl(212_60%_10%)] dark:shadow-[0_10px_34px_-8px_hsl(212_100%_60%/0.65)]',
          className,
        )}
        {...props}
      >
        {loading ? (
          <Loader2 aria-hidden="true" className="animate-spin motion-reduce:animate-none" />
        ) : null}
        {/* Translators may replace this text node. Keep a React-owned element
            as the sibling anchor when inserting/removing the loading icon. */}
        <span>{children}</span>
        {!loading ? (
          <ArrowRight
            aria-hidden="true"
            className="transition-transform duration-200 group-hover:translate-x-0.5 motion-reduce:transform-none motion-reduce:transition-none"
          />
        ) : null}
      </Button>
      {loading && loadingLabel ? (
        <span className="sr-only" role="status" aria-live="polite">
          {loadingLabel}
        </span>
      ) : null}
    </>
  );
}
